/* eslint-disable camelcase */
import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { UIHeading, UINotifier } from 'app/components/ui';
import { BonusesNotifier } from 'app/components/Bonuses';
import { useQuery, useReactiveVar } from '@apollo/client';
import { isDesktop } from 'app/utils/resolution';

import { isAsNewbieAuthedVar, selectedPeriodVar } from 'app/apollo/reaction';
import { useGetCashbackTestsValues } from 'app/hooks/useGetCashbackTestsValues';
import SubscriptionInfoPopup from 'app/components/infoPopup/SubscriptionInfoPopup';
import { useMenuPeriodsData } from 'app/hooks/accessors';
import {
    DishesPrice,
    DeliveryPrice,
    ReferralAmount,
    Promocode,
    Subscription,
    AppliedDiscountAmount,
    SubscriptionAppliedDiscountRow,
    PayByBonuses,
    Total,
} from './BasketCheckRows';

import './basket-check.scss';
import { PromocodeAppliedDiscountRow } from './BasketCheckRows/BasketCheckRowDeliveryDiscountRows';
import { GET_USER } from './graphql/GET_USER.graphql';
import { PromoAmoutWithoutForm } from './BasketCheckRows/BasketCheckRowPromocode';
import { _getIsRecurrentHiddenByPeriod } from 'app/views/Checkout/checkout.utils';
import { menu } from 'app/assets/dishesSelections/dishesSelections';
import Notifier from 'app/components/ui/Notifier';
import { FurtherPeriodNotifier } from '../FurtherPeriodNotifier';


export const locales = {
    ru: {
        heading: 'Ваш заказ',
    },
    en: {
        heading: 'Your order',
    },
};


const rowsConfig = [
    {
        id: 'DishesPrice',
        check: () => true,
        Row: DishesPrice,
    },
    {
        id: 'DeliveryPrice',
        check: () => true,
        Row: DeliveryPrice,
    },
    {
        id: 'ReferralAmount',
        check: (props) => {
            const { basket: { totals: { referral_amount: referralAmount } }, isCashbackTestGroup } = props;
            return !isCashbackTestGroup && referralAmount > 0;
        },
        Row: ReferralAmount,
    },
    {
        id: 'Subscription',
        check: (props) => {
            const {
                hideRecurrent: hideRecurrentProp,
                isSubscriptionHidden,
                hiddenElements: {
                    payment_button_recurrent: hideRecurrent,
                    payment_button_online: hideOnlinePayment,
                },
                basket: { totals: { static_common_price: staticCommonPrice } },
                hasMealprepDishesInCart,
            } = props;

            const isFreeOrder = staticCommonPrice === 0;
            const hideRecurrentPayment = isSubscriptionHidden || hideRecurrent || hideOnlinePayment
                || isFreeOrder || hideRecurrentProp || hasMealprepDishesInCart;

            return !hideRecurrentPayment;
        },
        Row: Subscription,
    },
    {
        id: 'PayByBonuses',
        check: (props) => {
            const { isCashbackTestGroup, isUserAuthed } = props;

            if (isCashbackTestGroup) {
                return isUserAuthed;
            }

            return false;
        },
        Row: PayByBonuses,
    },
    {
        id: 'Promocode',
        check: (props) => {
            const renderContextsCollection = ['basket', 'stepByStepCheckout', 'singleStepCheckout'];
            const { renderContext } = props;
            return renderContextsCollection.includes(renderContext);
        },
        Row: Promocode,
    },

    {
        id: 'PromocodeWithoutFrom',
        check: (props) => {
            const {
                basket: { totals: { promocodeDiscountAmount } },
            } = props;

            const renderContextsCollection = ['delivery-basket'];
            const { renderContext } = props;
            return renderContextsCollection.includes(renderContext) && Boolean(promocodeDiscountAmount);
        },
        Row: PromoAmoutWithoutForm,
    },
    {
        id: 'AppliedDiscountAmount',
        check: (props) => {
            const {
                renderContext,
                basket: { totals: { specialDiscountAmount } },
            } = props;

            return renderContext === 'delivery-basket' && specialDiscountAmount > 0;
        },
        Row: AppliedDiscountAmount,
    },

    /**
    * Закомментировано, т.к. AppliedDiscountAmount включает в себя и скидку по подписке тоже
    */
    // {
    //     id: 'SubscriptionAppliedDiscountRow',
    //     check: (props) => {
    //         const {
    //             renderContext,
    //             basket: { totals: { discount_amount: discountAmount } },
    //         } = props;

    //         return renderContext === 'delivery-basket' && discountAmount > 0;
    //     },
    //     Row: SubscriptionAppliedDiscountRow,
    // },
    // {
    //     id: 'PromocodeAppliedDiscountRow',
    //     check: (props) => {
    //         const {
    //             renderContext,
    //         } = props;

    //         return renderContext === 'stepByStepCheckout';
    //     },
    //     Row: PromocodeAppliedDiscountRow,
    // },

    {
        id: 'Total',
        check: () => true,
        Row: Total,
    },
];

export const BasketCheck = ({
    locale,
    subdivision = 'RU-MOW',
    ...restProps
}) => {
    const { data } = useQuery(GET_USER);
    const [showNotifier, setShowNotifier] = useState(false);

    const menuFilter = useMenuPeriodsData();
    const selectedPeriod = useReactiveVar(selectedPeriodVar);

    const [isCashbackTestGroup] = useGetCashbackTestsValues();
    const isAsNewbieAuthed = useReactiveVar(isAsNewbieAuthedVar);

    /* SELECTORS */
    const isUserAuthed = useMemo(
        () => Boolean(data.user || isAsNewbieAuthed),
        [data.user, isAsNewbieAuthed],
    );

    const isSubscriptionHidden = useMemo(
        () => _getIsRecurrentHiddenByPeriod(menuFilter, selectedPeriod),
        [menuFilter, selectedPeriod],
    );

    // BasketSubscriptionInfoPopup

    const rows = useMemo(
        () => rowsConfig.filter((rowData) => rowData.check({
            subdivision,
            isUserAuthed,
            isCashbackTestGroup,
            isSubscriptionHidden,
            ...restProps,
        })),
        [restProps, isUserAuthed, subdivision, isCashbackTestGroup, isSubscriptionHidden],
    );

    const isBonusesNotifier = useMemo(
        () => {
            const isAmount = restProps?.basket?.totals?.cashbackReward;
            return isCashbackTestGroup && isUserAuthed && isAmount;
        }, [isUserAuthed, restProps?.basket?.totals?.cashbackReward, isCashbackTestGroup],
    );

    const hasBonuses = useMemo(
        () => restProps?.basket?.totals?.referral_amount,
        [],
    );

    /* HANDLERS */
    const setRedirectingTargetPage = () => {
        sessionStorage.setItem('redirect_from_auth', 'basket');
    };

    return (
        <div styleName="basket-check__root">
            <UIHeading level="3.2" styleName="basket-check__heading">
                {locales[locale].heading}
            </UIHeading>
            <FurtherPeriodNotifier context="check" cb={setShowNotifier} />
            {!isUserAuthed && (
                <a href="/auth" onClick={setRedirectingTargetPage}>
                    <div styleName="bonuses-notifier__heading-wrapper">
                        <BonusesNotifier isUserAuthorized={false} />
                    </div>
                </a>
            )}
            <div>
                {rows.map(({ id, Row }, i) => {
                    const isPromocodeSection = id === 'Promocode';
                    if (isPromocodeSection && isCashbackTestGroup && hasBonuses) return null;
                    return (
                        <div styleName={`basket-check__item ${showNotifier && i === 0 ? 'noBorder' : ''}`} key={id}>
                            <Row
                                locale={locale}
                                subdivision={subdivision}
                                {...restProps}
                            />
                        </div>
                    );
                })}
            </div>
            {isBonusesNotifier && (
                <div styleName="bonuses-notifier__bottom-wrapper">
                    <BonusesNotifier
                        isUserAuthorized={isUserAuthed}
                        bonusesAmount={restProps?.basket?.totals?.cashbackReward}
                    />
                </div>
            )}
        </div>
    );
};

BasketCheck.propTypes = {
    locale: PropTypes.string.isRequired,

    basket: PropTypes.shape({
        totals: PropTypes.shape({
            total_price: PropTypes.number.isRequired,
            delivery_price: PropTypes.number.isRequired,
            referral_amount: PropTypes.number.isRequired,
            total_common_price: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,

    hiddenElements: PropTypes.shape({
        payment_button_recurrent: PropTypes.bool,
        payment_button_online: PropTypes.bool,
    }).isRequired,

    subdivision: PropTypes.string,
    onClickDeliveryPrice: PropTypes.func.isRequired,
};
